import { useState, type ReactNode, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../utils/api-client';

interface OnboardingStep {
  id: string;
  title: string;
  component: ReactNode;
}

export function MerchantOnboardingPage(): ReactNode {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    website: '',
    description: '',
    category: '',
    commissionType: 'percentage' as 'percentage' | 'fixed',
    commissionValue: '',
    cookieDuration: '30',
    contactEmail: '',
    contactPhone: '',
  });

  const submitMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      await api.post('/marketplace/merchant/onboard', formData);
    },
  });

  const steps: OnboardingStep[] = [
    {
      id: 'business-info',
      title: 'Business Information',
      component: (
        <BusinessInfoStep
          data={formData}
          onChange={(updates) => setFormData({ ...formData, ...updates })}
          onNext={() => setCurrentStep(1)}
        />
      ),
    },
    {
      id: 'offer-details',
      title: 'Offer Details',
      component: (
        <OfferDetailsStep
          data={formData}
          onChange={(updates) => setFormData({ ...formData, ...updates })}
          onNext={() => setCurrentStep(2)}
        />
      ),
    },
    {
      id: 'contact',
      title: 'Contact Information',
      component: (
        <ContactStep
          data={formData}
          onChange={(updates) => setFormData({ ...formData, ...updates })}
          onSubmit={() => void submitMutation.mutate()}
          isLoading={submitMutation.isPending}
        />
      ),
    },
  ];

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">List Your Offer</h2>
        <p className="text-gray-500">
          Join our marketplace and reach thousands of affiliate marketers
        </p>
      </div>

      {/* Progress Bar */}
      <div className="rounded-lg border bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{steps[currentStep]?.title}</h3>
          <span className="text-sm text-gray-500">Step {currentStep + 1} of {steps.length}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-4 flex items-center justify-between">
          {steps.map((step, i) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  i <= currentStep
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {i < currentStep ? '✓' : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`h-1 w-16 mx-2 ${
                    i < currentStep ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="rounded-lg border bg-white p-6">
        {steps[currentStep]?.component}
      </div>
    </div>
  );
}

function BusinessInfoStep({
  data,
  onChange,
  onNext,
}: {
  data: typeof MerchantOnboardingPage.prototype.formData;
  onChange: (updates: Partial<typeof data>) => void;
  onNext: () => void;
}): ReactNode {
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (data.businessName && data.businessType && data.website) {
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
          Business Name *
        </label>
        <input
          type="text"
          id="businessName"
          value={data.businessName}
          onChange={(e) => onChange({ businessName: e.target.value })}
          required
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          placeholder="Your Company Name"
        />
      </div>

      <div>
        <label htmlFor="businessType" className="block text-sm font-medium text-gray-700">
          Business Type *
        </label>
        <select
          id="businessType"
          value={data.businessType}
          onChange={(e) => onChange({ businessType: e.target.value })}
          required
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select type...</option>
          <option value="saas">SaaS</option>
          <option value="ecommerce">E-commerce</option>
          <option value="course">Online Course</option>
          <option value="service">Service Provider</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div>
        <label htmlFor="website" className="block text-sm font-medium text-gray-700">
          Website URL *
        </label>
        <input
          type="url"
          id="website"
          value={data.website}
          onChange={(e) => onChange({ website: e.target.value })}
          required
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          placeholder="https://example.com"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Business Description
        </label>
        <textarea
          id="description"
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={4}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          placeholder="Tell us about your business..."
        />
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Continue
        </button>
      </div>
    </form>
  );
}

function OfferDetailsStep({
  data,
  onChange,
  onNext,
}: {
  data: typeof MerchantOnboardingPage.prototype.formData;
  onChange: (updates: Partial<typeof data>) => void;
  onNext: () => void;
}): ReactNode {
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (data.category && data.commissionValue) {
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
          Category *
        </label>
        <select
          id="category"
          value={data.category}
          onChange={(e) => onChange({ category: e.target.value })}
          required
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select category...</option>
          <option value="health">Health & Wellness</option>
          <option value="finance">Finance</option>
          <option value="software">Software</option>
          <option value="ecommerce">E-commerce</option>
          <option value="education">Education</option>
          <option value="travel">Travel</option>
          <option value="fitness">Fitness</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="commissionType" className="block text-sm font-medium text-gray-700">
            Commission Type *
          </label>
          <select
            id="commissionType"
            value={data.commissionType}
            onChange={(e) => onChange({ commissionType: e.target.value as typeof data.commissionType })}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </div>

        <div>
          <label htmlFor="commissionValue" className="block text-sm font-medium text-gray-700">
            Commission Value *
          </label>
          <input
            type="number"
            id="commissionValue"
            value={data.commissionValue}
            onChange={(e) => onChange({ commissionValue: e.target.value })}
            required
            min="0"
            step="0.01"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder={data.commissionType === 'percentage' ? '10' : '50'}
          />
          <p className="mt-1 text-xs text-gray-500">
            {data.commissionType === 'percentage' ? 'Percentage (e.g., 10 for 10%)' : 'Fixed amount in USD'}
          </p>
        </div>
      </div>

      <div>
        <label htmlFor="cookieDuration" className="block text-sm font-medium text-gray-700">
          Cookie Duration (days)
        </label>
        <input
          type="number"
          id="cookieDuration"
          value={data.cookieDuration}
          onChange={(e) => onChange({ cookieDuration: e.target.value })}
          min="1"
          max="365"
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <p className="mt-1 text-xs text-gray-500">
          How long should affiliate cookies last? (default: 30 days)
        </p>
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={() => {/* Go to previous step */}}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="submit"
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Continue
        </button>
      </div>
    </form>
  );
}

function ContactStep({
  data,
  onChange,
  onSubmit,
  isLoading,
}: {
  data: typeof MerchantOnboardingPage.prototype.formData;
  onChange: (updates: Partial<typeof data>) => void;
  onSubmit: () => void;
  isLoading: boolean;
}): ReactNode {
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (data.contactEmail) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
        <div className="flex items-start">
          <svg className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-primary-900">Almost Done!</h4>
            <p className="mt-1 text-sm text-primary-700">
              We'll review your submission and get back to you within 24 hours. Once approved, your offer will be live in our marketplace.
            </p>
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
          Contact Email *
        </label>
        <input
          type="email"
          id="contactEmail"
          value={data.contactEmail}
          onChange={(e) => onChange({ contactEmail: e.target.value })}
          required
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          placeholder="contact@example.com"
        />
        <p className="mt-1 text-xs text-gray-500">
          We'll use this email to contact you about your submission
        </p>
      </div>

      <div>
        <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
          Contact Phone (optional)
        </label>
        <input
          type="tel"
          id="contactPhone"
          value={data.contactPhone}
          onChange={(e) => onChange({ contactPhone: e.target.value })}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          placeholder="+1 (555) 123-4567"
        />
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={() => {/* Go to previous step */}}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {isLoading ? 'Submitting...' : 'Submit for Review'}
        </button>
      </div>
    </form>
  );
}
